import { useKeyPress } from 'ahooks'
import cn from 'classnames'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import InputNumber from 'components/uiKit/InputNumber'
import { isMixed } from 'components/uiKit/KitUtils'
import { testPropsEl } from 'utils/test/qaData'

import { AdditionWrapper } from '../Input'
import type { IKitControlProps } from '../KitTypes'
import { KitSize } from '../KitTypes'
import * as s from './Slider.module.scss'
import { calculatePercentage, getPosition } from './utils'

export interface ISliderHint {
  label: string
  value: number
}

export interface ISliderProps extends IKitControlProps<number> {
  /**
   * Determines whether to show an input field for manual value input.
   */
  showInput?: boolean

  /**
   * The placeholder text for the input field.
   */
  placeholder?: string

  /**
   * The postfix text to display after the slider value.
   */
  postfix?: React.ReactNode

  /**
   * The maximum value of the slider.
   */
  max?: number

  /**
   * The minimum value of the slider.
   */
  min?: number

  /**
   * Step of the slider.
   */
  step?: number

  /**
   * Clickable hints displayed below the slider.
   */
  hints?: ISliderHint[]
  /** The width of the input field in pixels.
   * @default 64
   */
  inputWidth?: number
}

const Slider: React.FC<ISliderProps> = ({
  name,
  onChange,
  onBlur,
  onFocus,
  min = 0,
  max = 100,
  step = 1,
  defaultValue,
  value = defaultValue ?? R.clamp(min, max, 0),
  size = KitSize.M,
  disabled,
  readOnly,
  showInput,
  postfix,
  placeholder,
  inputWidth = 64,
  error = false,
  hints,
}) => {
  const safeValue = isMixed(value) ? null : ((value as number | null | undefined) ?? null)

  const [localValue, setLocalValue] = useState<number | null>(safeValue)
  const sliderRef = useRef<HTMLDivElement | null>(null)
  const rootSliderRef = useRef<HTMLDivElement | null>(null)
  const percentage = useMemo(
    () => calculatePercentage({ value: safeValue ?? 0, localValue, min, max }),
    [safeValue, localValue, min, max],
  )

  const handleChange = useCallback(
    (value: number | null) => {
      setLocalValue(value)
      if (R.isNotNil(value) && value >= min && value <= max) {
        onChange?.(value)
      }
    },
    [onChange, max, min],
  )

  const calculateNewValue = useCallback(
    (clientX: number) => {
      const { left, width } = sliderRef.current?.getBoundingClientRect() || { left: 0, width: 0 }
      const mouseX = clientX - left

      const percentage = (mouseX / width) * 100
      const calcNewValue = Math.round(((percentage / 100) * (max - min)) / step) * step + min

      const newValue = R.clamp(min, max, calcNewValue)
      handleChange?.(newValue)
    },
    [handleChange, min, step, max],
  )

  const handleClick = (e: React.MouseEvent) => {
    if (!disabled && !readOnly) {
      calculateNewValue(e.clientX)
    }
  }

  const handleInputChange = useCallback(
    (value: number | null) => handleChange?.(value),
    [handleChange],
  )

  const handleInputBlur = useCallback(
    <E,>(e?: React.FocusEvent<E>) => {
      onBlur?.(e)
      if (localValue === null || localValue < min || localValue > max) {
        setLocalValue(safeValue)
      }
    },
    [onBlur, max, min, safeValue, localValue],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      if (disabled || readOnly) {
        return
      }

      let animationFrameId: number | null = null

      const onMouseMove = (e: MouseEvent) => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
        }
        animationFrameId = requestAnimationFrame(() => {
          const { clientX } = getPosition(e)
          calculateNewValue(clientX)
        })
      }

      const onMouseUp = (event: MouseEvent) => {
        event.stopPropagation()
        document.removeEventListener('mouseup', onMouseUp, true)
        document.removeEventListener('mousemove', onMouseMove, true)
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
      }

      document.addEventListener('mouseup', onMouseUp, true)
      document.addEventListener('mousemove', onMouseMove, true)
    },
    [calculateNewValue, disabled, readOnly],
  )

  useKeyPress(
    ['leftarrow', 'rightarrow'],
    (e) => {
      e.preventDefault()
      if (safeValue !== null && e.key === 'ArrowLeft' && safeValue - step >= min) {
        handleChange?.(safeValue - step)
      }

      if (safeValue !== null && e.key === 'ArrowRight' && safeValue + step <= max) {
        handleChange?.(safeValue + step)
      }
    },
    { target: rootSliderRef },
  )

  const handleHintClick = useCallback(
    (hintValue: number) => {
      if (!disabled && !readOnly) {
        handleChange?.(hintValue)
      }
    },
    [handleChange, disabled, readOnly],
  )

  useEffect(() => setLocalValue(safeValue), [safeValue])

  return (
    <div
      {...testPropsEl('slider', { name, value: safeValue })}
      className={cn(s.root, s[size], {
        [s.disabled]: disabled,
        [s.hasHint]: hints && hints.length > 0,
      })}
    >
      <div className={s.sliderRow}>
        <div
          className={s.sliderWrapper}
          onBlur={onBlur}
          onClick={handleClick}
          onFocus={onFocus}
          ref={rootSliderRef}
          tabIndex={0}
        >
          <div className={cn(s.slider, s[size])} onMouseDown={handleMouseDown} ref={sliderRef}>
            <div className={s.sliderRail} />
            <div className={s.sliderTrack} style={{ width: `${percentage}%` }} />
            <div className={s.sliderStep} />
            <div className={s.sliderHandle} style={{ left: `${percentage}%` }} />
          </div>
        </div>
        {showInput && (
          <div className={s.inputWrapper} style={{ width: inputWidth }}>
            <InputNumber
              disabled={disabled}
              error={error || localValue === null || localValue < min || localValue > max}
              name={name}
              onBlur={handleInputBlur}
              onChange={handleInputChange}
              onFocus={onFocus}
              placeholder={placeholder}
              postfix={postfix ? <AdditionWrapper>{postfix}</AdditionWrapper> : null}
              size={size}
              value={Number(localValue?.toFixed(2))}
            />
          </div>
        )}
      </div>
      {hints && hints.length > 0 && (
        <div className={s.hints}>
          {hints.map((hint) => (
            <div className={s.hint} key={hint.value} onClick={() => handleHintClick(hint.value)}>
              {hint.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Slider
